import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleCallback, OAuthError } from '@goodsteward/gs-auth-spa';
import { useAuthStore } from '@/stores/auth';
import { fetchMe } from '@/api/auth';
import { getCognitoImpersonationConfig } from '@/auth/config';
import { AuthShell } from './AuthShell';
import { Alert } from '@/components/ui/Alert';

// /impersonate-callback — Cognito redirects here with ?code=&state=
// after the staffer completes the OAuth dance against the dedicated
// impersonation app client (per ADR 0007).
//
// Same flow as the regular /auth/callback — exchange code for tokens,
// stash, fetch /api/me, then route. The difference is the OAuth
// CONFIG: this uses the impersonation client_id and the impersonation
// redirect URI. PMA's middleware sees `acting_as_practice_id` on the
// resulting JWT and treats the rest of the session as impersonation
// (full perms, audit-tagged as gs_staff).

export function ImpersonateCallbackPage() {
    const setCognitoSession = useAuthStore((s) => s.setCognitoSession);
    const setCognitoTokens  = useAuthStore((s) => s.setCognitoTokens);
    const nav = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;
        (async () => {
            try {
                const config = getCognitoImpersonationConfig();
                const result = await handleCallback(config);
                setCognitoTokens({
                    accessToken:  result.accessToken,
                    refreshToken: result.refreshToken,
                    expiresAt:    result.expiresAt,
                });
                const me = await fetchMe();
                setCognitoSession(
                    {
                        accessToken:  result.accessToken,
                        refreshToken: result.refreshToken,
                        expiresAt:    result.expiresAt,
                    },
                    me.user,
                    me.practice,
                );
                // Always route to '/' for impersonation — the staffer
                // is entering an unfamiliar tenant; the dashboard is
                // the right landing surface.
                nav('/', { replace: true });
            } catch (e) {
                const msg = e instanceof OAuthError
                    ? e.message
                    : e instanceof Error ? e.message : String(e);
                setError(msg);
            }
        })();
    }, [setCognitoSession, setCognitoTokens, nav]);

    return (
        <AuthShell showNotice={false}>
            {error ? (
                <Alert variant="danger">Impersonation sign-in failed: {error}</Alert>
            ) : (
                <p className="text-sm text-text-subtle">
                    Loading impersonation session…
                </p>
            )}
        </AuthShell>
    );
}
